import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Minus, Package, Plus, Save, Truck } from 'lucide-react';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { useDataVersion } from '../../services/dataChangeBus';
import {
  hardwareApi, type DeviceModelRow, type DeviceOrderItemRow, type DeviceUnitRow,
} from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';

/** serialized-vs-quantity-tracking wave — a quantity-mode model has no unit rows. */
const isQty = (m: Pick<DeviceModelRow, 'trackingMode'> | undefined) => m?.trackingMode === 'quantity';

/**
 * PACKING & BOXES — mockup screen D. Mounted inside `OrderDetail.tsx` (the
 * order-detail flow), behind a "Pack this order" toggle. Box CONTENTS are
 * always DERIVED from `GET /lifecycle/orders/{id}/packing` — never tracked
 * client-side — so this panel re-fetches after every mutation rather than
 * mutating local state optimistically.
 */

interface Props {
  orderId: string;
  models: DeviceModelRow[];
  onChanged: () => void;
}

const PackOrderPanel: React.FC<Props> = ({ orderId, models, onChanged }) => {
  const [boxes, setBoxes] = useState<{ boxNo: number; carrier?: string; trackingNumber?: string; trackingUrl?: string; weightGrams?: number; packedAt?: number; unitIds: string[]; bulkLines?: { modelId: string; qty: number }[] }[]>([]);
  const [unassignedUnitIds, setUnassignedUnitIds] = useState<string[]>([]);
  const [units, setUnits] = useState<DeviceUnitRow[]>([]);
  const [items, setItems] = useState<DeviceOrderItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingBox, setAddingBox] = useState(false);
  const [packError, setPackError] = useState<Record<number, string>>({});
  const [packing, setPacking] = useState<string | null>(null);
  const [pickerBox, setPickerBox] = useState<number | null>(null);
  const [pickUnitId, setPickUnitId] = useState<string>('');
  // Bulk (quantity-mode) packing — one model + qty at a time, per box.
  const [bulkPickerBox, setBulkPickerBox] = useState<number | null>(null);
  const [pickBulkModelId, setPickBulkModelId] = useState<string>('');
  const [pickBulkQty, setPickBulkQty] = useState<string>('1');
  const [packingBulk, setPackingBulk] = useState(false);
  const [editingBox, setEditingBox] = useState<number | null>(null);
  const [editCarrier, setEditCarrier] = useState('');
  const [editTracking, setEditTracking] = useState('');
  const [savingBox, setSavingBox] = useState(false);
  const dataVersion = useDataVersion();
  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    // Spinner on the first read only — a re-read swaps the data in place.
    if (!loadedOnce.current) setLoading(true);
    setError(null);
    try {
      const [pack, ord] = await Promise.all([
        hardwareApi.getPacking(orderId),
        hardwareApi.getOrder(orderId),
      ]);
      setBoxes(pack.boxes || []);
      setUnassignedUnitIds(pack.unassignedUnitIds || []);
      setUnits(ord.units || []);
      setItems(ord.order?.items || []);
      loadedOnce.current = true;
    } catch (e: any) {
      setError(e?.message || 'Failed to load packing for this order');
    } finally {
      setLoading(false);
    }
  }, [orderId, dataVersion]); // dataVersion: re-read after any save on the page

  useEffect(() => { load(); }, [load]);

  const unitOf = (unitId: string) => units.find((u) => u.unitId === unitId);
  const modelOf = (modelId: string) => models.find((m) => m.modelId === modelId);
  const modelName = (modelId: string) => modelOf(modelId)?.modelName || modelId;

  // Only CONFIGURED units (or already `packing`, to support moving between
  // boxes) can be picked — mirrors the server's own gate exactly, per the
  // guard order documented in b1-lifecycle-report.md.
  const packableUnits = useMemo(
    () => units.filter((u) => u.state === 'configured' || u.state === 'packing'),
    [units],
  );

  /**
   * Remaining-to-pack per quantity-mode model on this order: what was
   * ordered minus what is already sitting in a box's `bulkLines`. There is
   * no dedicated "reserved remainder" read (only `POST .../allocate`, which
   * MUTATES) — this is the same number by a different, side-effect-free
   * route, and the server is still the one that refuses an over-pack.
   */
  const bulkRemainingByModel = useMemo(() => {
    const needed = new Map<string, number>();
    for (const it of items) {
      if (!it.modelId || !isQty(modelOf(it.modelId))) continue;
      needed.set(it.modelId, (needed.get(it.modelId) || 0) + (it.quantity || 0));
    }
    for (const box of boxes) {
      for (const bl of box.bulkLines || []) {
        needed.set(bl.modelId, (needed.get(bl.modelId) || 0) - bl.qty);
      }
    }
    return needed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, boxes, models]);

  const bulkModelsWithRemainder = useMemo(
    () => Array.from(bulkRemainingByModel.entries()).filter(([, qty]) => qty > 0),
    [bulkRemainingByModel],
  );

  const packBulk = async (boxNo: number) => {
    const remainder = bulkRemainingByModel.get(pickBulkModelId) || 0;
    const qty = Math.min(remainder, Number.parseInt(pickBulkQty, 10) || 0);
    if (!pickBulkModelId || qty < 1) return;
    setPackingBulk(true);
    setPackError((prev) => ({ ...prev, [boxNo]: '' }));
    try {
      await hardwareApi.packBulkIntoBox(orderId, boxNo, pickBulkModelId, qty);
      setPickBulkModelId('');
      setPickBulkQty('1');
      setBulkPickerBox(null);
      await load();
      onChanged();
      toastService.success(`Packed ${qty}.`);
    } catch (e: any) {
      // Gate errors (agreement unsigned / cancelled order / over-pack) are
      // shown VERBATIM — the server's own message is the honest one here.
      setPackError((prev) => ({ ...prev, [boxNo]: e?.message || 'Could not pack this quantity' }));
    } finally {
      setPackingBulk(false);
    }
  };

  const addBox = async () => {
    setAddingBox(true);
    setError(null);
    try {
      await hardwareApi.upsertBox(orderId, {});
      await load();
      toastService.success('Box added.');
    } catch (e: any) {
      setError(e?.message || 'Failed to add a box');
    } finally {
      setAddingBox(false);
    }
  };

  const openEdit = (box: typeof boxes[number]) => {
    setEditingBox(box.boxNo);
    setEditCarrier(box.carrier || '');
    setEditTracking(box.trackingNumber || '');
  };

  const saveBoxFields = async (boxNo: number) => {
    setSavingBox(true);
    try {
      await hardwareApi.upsertBox(orderId, { boxNo, carrier: editCarrier || undefined, trackingNumber: editTracking || undefined });
      setEditingBox(null);
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to save box details');
    } finally {
      setSavingBox(false);
    }
  };

  const packInto = async (boxNo: number) => {
    if (!pickUnitId) return;
    setPacking(pickUnitId);
    setPackError((prev) => ({ ...prev, [boxNo]: '' }));
    try {
      await hardwareApi.packUnitIntoBox(orderId, boxNo, pickUnitId);
      setPickUnitId('');
      setPickerBox(null);
      await load();
      onChanged();
      toastService.success('Unit packed.');
    } catch (e: any) {
      // Gate errors (agreement unsigned / provisioning incomplete) are shown
      // VERBATIM — the server's own message is the honest one here.
      setPackError((prev) => ({ ...prev, [boxNo]: e?.message || 'Could not pack this unit' }));
    } finally {
      setPacking(null);
    }
  };

  if (loading) {
    return <RubyLoader variant="section" />;
  }

  return (
    <div className="rounded-xl border-2 bg-white p-5 text-gray-900 mt-4" style={{ borderColor: '#DC143C' }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Package size={16} /> Packing & boxes</h3>
        <button
          onClick={addBox}
          disabled={addingBox}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50 min-h-[36px]"
          style={{ background: '#DC143C' }}
        >
          {addingBox ? <RubyGemMark size={13} drawing color="currentColor" /> : <Plus size={13} />} Add box
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {boxes.length === 0 ? (
        <p className="text-xs text-gray-500 py-6 text-center">No boxes yet — add one to start packing this order.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {boxes.map((box) => (
            <div key={box.boxNo} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-800">Box {box.boxNo}</span>
                <span className={`text-[9.5px] font-semibold rounded-full px-2 py-0.5 border ${
                  box.trackingNumber ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'
                }`}>{box.trackingNumber ? 'labeled' : 'open'}</span>
              </div>
              <ul className="space-y-1 mb-2">
                {box.unitIds.length === 0 && (box.bulkLines || []).length === 0 && <li className="text-[11px] text-gray-400">Empty</li>}
                {box.unitIds.map((uid) => {
                  const u = unitOf(uid);
                  return (
                    <li key={uid} className="text-[11px] font-mono text-gray-700 border-t border-dashed border-gray-200 pt-1 first:border-t-0 first:pt-0">
                      {uid} {u ? `· ${modelName(u.modelId)}` : ''}
                    </li>
                  );
                })}
                {(box.bulkLines || []).map((bl) => (
                  <li key={bl.modelId} className="text-[11px] font-mono text-gray-700 border-t border-dashed border-gray-200 pt-1 first:border-t-0 first:pt-0">
                    {bl.qty}× {modelName(bl.modelId)}
                  </li>
                ))}
              </ul>

              {pickerBox === box.boxNo ? (
                <div className="space-y-1.5">
                  <SearchableDropdown
                    options={packableUnits
                      .filter((u) => !boxes.some((b) => b.unitIds.includes(u.unitId)))
                      .map((u) => ({ value: u.unitId, label: `${u.unitId} · ${modelName(u.modelId)}` }))}
                    value={pickUnitId || null}
                    onChange={(v) => setPickUnitId(v || '')}
                    placeholder="-- Pick a configured unit --"
                    variant="light"
                    compact
                  />
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => packInto(box.boxNo)}
                      disabled={!pickUnitId || packing === pickUnitId}
                      className="flex-1 px-2 py-1.5 rounded-lg text-white text-[10.5px] font-semibold disabled:opacity-40"
                      style={{ background: '#DC143C' }}
                    >
                      {packing === pickUnitId ? <span className="inline-flex"><RubyGemMark size={11} drawing color="currentColor" /></span> : 'Pack it'}
                    </button>
                    <button onClick={() => { setPickerBox(null); setPickUnitId(''); }} className="px-2 py-1.5 rounded-lg border border-gray-300 text-[10.5px] font-medium text-gray-600">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setPickerBox(box.boxNo)}
                  className="w-full text-left text-[11px] font-semibold text-amber-700 hover:text-amber-800"
                >
                  ＋ scan / pick next unit…
                </button>
              )}

              {bulkModelsWithRemainder.length > 0 && (
              bulkPickerBox === box.boxNo ? (
                <div className="mt-1.5 space-y-1.5 border-t border-dashed border-gray-200 pt-1.5">
                  <SearchableDropdown
                    options={bulkModelsWithRemainder.map(([modelId, remaining]) => ({
                      value: modelId, label: `${modelName(modelId)} · ${remaining} remaining`,
                    }))}
                    value={pickBulkModelId || null}
                    onChange={(v) => { setPickBulkModelId(v || ''); setPickBulkQty(String(Math.min(1, bulkRemainingByModel.get(v || '') || 1))); }}
                    placeholder="-- Pick a quantity item --"
                    variant="light"
                    compact
                  />
                  {pickBulkModelId && (
                    <div className="flex items-center gap-1.5">
                      <div className="inline-flex items-center overflow-hidden rounded-lg border border-gray-300">
                        <button onClick={() => setPickBulkQty((q) => String(Math.max(1, (Number.parseInt(q, 10) || 1) - 1)))} className="w-6 h-7 bg-gray-50 text-gray-700"><Minus size={11} className="mx-auto" /></button>
                        <input
                          value={pickBulkQty}
                          onChange={(e) => setPickBulkQty(e.target.value.replace(/\D/g, ''))}
                          className="w-12 text-center font-mono text-[10.5px] py-1 outline-none"
                        />
                        <button onClick={() => setPickBulkQty((q) => String(Math.min(bulkRemainingByModel.get(pickBulkModelId) || 1, (Number.parseInt(q, 10) || 1) + 1)))} className="w-6 h-7 bg-gray-50 text-gray-700"><Plus size={11} className="mx-auto" /></button>
                      </div>
                      <span className="text-[10px] text-gray-500">of {bulkRemainingByModel.get(pickBulkModelId) || 0} left</span>
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => packBulk(box.boxNo)}
                      disabled={!pickBulkModelId || packingBulk}
                      className="flex-1 px-2 py-1.5 rounded-lg text-white text-[10.5px] font-semibold disabled:opacity-40"
                      style={{ background: '#DC143C' }}
                    >
                      {packingBulk ? <span className="inline-flex"><RubyGemMark size={11} drawing color="currentColor" /></span> : 'Pack qty into box'}
                    </button>
                    <button onClick={() => { setBulkPickerBox(null); setPickBulkModelId(''); }} className="px-2 py-1.5 rounded-lg border border-gray-300 text-[10.5px] font-medium text-gray-600">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setBulkPickerBox(box.boxNo)}
                  className="mt-1 w-full text-left text-[11px] font-semibold text-amber-700 hover:text-amber-800"
                >
                  ＋ pack a quantity item…
                </button>
              )
              )}
              {packError[box.boxNo] && (
                <p className="mt-1.5 text-[10.5px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5">
                  {packError[box.boxNo]}
                </p>
              )}

              {editingBox === box.boxNo ? (
                <div className="mt-2 space-y-1.5 border-t border-gray-200 pt-2">
                  <input value={editCarrier} onChange={(e) => setEditCarrier(e.target.value)} placeholder="Carrier" className="w-full px-2 py-1.5 border border-gray-300 rounded text-[10.5px]" />
                  <input value={editTracking} onChange={(e) => setEditTracking(e.target.value)} placeholder="Tracking number" className="w-full px-2 py-1.5 border border-gray-300 rounded text-[10.5px] font-mono" />
                  <div className="flex gap-1.5">
                    <button onClick={() => saveBoxFields(box.boxNo)} disabled={savingBox} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-white text-[10.5px] font-semibold" style={{ background: '#DC143C' }}>
                      {savingBox ? <RubyGemMark size={11} drawing color="currentColor" /> : <Save size={11} />} Save
                    </button>
                    <button onClick={() => setEditingBox(null)} className="px-2 py-1.5 rounded-lg border border-gray-300 text-[10.5px] font-medium text-gray-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => openEdit(box)} className="mt-2 flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700">
                  <Truck size={11} />
                  {box.trackingNumber ? `${box.carrier || ''} ${box.trackingNumber}` : 'no label yet — click to add carrier/tracking'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {unassignedUnitIds.length > 0 && (
        <p className="mt-3 text-[11px] text-gray-500">
          {unassignedUnitIds.length} unit(s) on this order are not yet in any box.
        </p>
      )}
      <p className="mt-3 text-[11px] text-gray-500">
        A unit is in exactly one box — re-packing moves it. Packing is refused while the finance agreement is unsigned
        or the unit's provisioning is incomplete; that refusal is shown above, word for word from the server.
      </p>
    </div>
  );
};

export default PackOrderPanel;
